// Reusable presentational components. Dark-first.
import React from "react";
import { Link } from "react-router-dom";

const OVERLINE_COLOR_CLASSES = {
  yellow: "text-[#FFC600]",
  white: "text-white",
  black: "text-black",
  default: "text-white/55",
};

export const Overline = ({ children, className = "", color = "default", ...rest }) => {
  const cls = OVERLINE_COLOR_CLASSES[color] || OVERLINE_COLOR_CLASSES.default;
  return <div className={`k-overline ${cls} ${className}`} {...rest}>{children}</div>;
};

export const SectionTitle = ({ kicker, title, intro, dark = true, testId }) => (
  <div data-testid={testId} className="max-w-3xl">
    {kicker ? (
      <Overline color={dark ? "yellow" : "black"} className="mb-4">{kicker}</Overline>
    ) : null}
    <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tighter ${dark ? "text-white" : "text-black"}`}>
      {title}
    </h2>
    {intro ? (
      <p className={`mt-5 text-base lg:text-lg ${dark ? "text-white/70" : "text-black/70"}`}>
        {intro}
      </p>
    ) : null}
  </div>
);

// Primary CTA: yellow surface, BLACK text (WCAG AAA contrast: 8.6:1).
export const PrimaryButton = ({ to, href, target, children, testId, onClick, type, className = "" }) => {
  const cls =
    "inline-flex items-center justify-center bg-[#FFC600] text-black font-bold px-7 py-4 border-2 border-[#FFC600] hover:bg-transparent hover:text-[#FFC600] transition-colors text-sm tracking-wide " +
    className;
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls} onClick={onClick}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={target === "_blank" ? "noreferrer" : undefined}
        data-testid={testId}
        className={cls}
      >
        {children}
      </a>
    );
  }
  return (
    <button type={type || "button"} data-testid={testId} className={cls} onClick={onClick}>
      {children}
    </button>
  );
};

// Secondary CTA: outlined on dark surface by default.
export const SecondaryButton = ({ to, href, target, children, testId, dark = true, className = "" }) => {
  const cls = `inline-flex items-center justify-center font-semibold px-7 py-4 border-2 text-sm tracking-wide transition-colors ${
    dark
      ? "border-white/40 text-white hover:border-[#FFC600] hover:text-[#FFC600]"
      : "border-black text-black hover:border-[#FFC600] hover:bg-black hover:text-[#FFC600]"
  } ${className}`;
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls}>{children}</Link>
    );
  }
  return (
    <a href={href} target={target} rel={target === "_blank" ? "noreferrer" : undefined} data-testid={testId} className={cls}>
      {children}
    </a>
  );
};

export const Tag = ({ children, className = "" }) => (
  <span className={`inline-block text-[11px] font-semibold uppercase tracking-[0.18em] px-3 py-1 border border-white/20 text-white/80 ${className}`}>
    {children}
  </span>
);

export const Crumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="text-xs uppercase tracking-[0.18em] text-white/50">
    {items.map((it, idx) => (
      <span key={it.label || `crumb-${idx}`}>
        {it.to ? (
          <Link to={it.to} className="hover:text-[#FFC600]">{it.label}</Link>
        ) : (
          <span className="text-white/35">{it.label}</span>
        )}
        {idx < items.length - 1 ? <span className="mx-2 text-white/30">/</span> : null}
      </span>
    ))}
  </nav>
);
